import React from 'react';
import { Link } from 'react-router-dom';
import './AboutUs.css';

const stats = [
  { value: '5000+', label: 'Happy Customers' },
  { value: '200+', label: 'Products Crafted' },
  { value: '50+', label: 'Corporate Clients' },
  { value: '100%', label: 'Made with Love' },
];

const values = [
  {
    icon: '✦',
    title: 'Personalisation First',
    desc: 'Every item is crafted to feel uniquely yours. From names to messages, we make it personal.',
  },
  {
    icon: '🎁',
    title: 'Curated Quality',
    desc: 'We select only premium materials and designs that leave a lasting, memorable impression.',
  },
  {
    icon: '🏭',
    title: 'In-House Production',
    desc: 'Design, production, and packaging — all managed under one roof for consistent excellence.',
  },
  {
    icon: '💼',
    title: 'Corporate Excellence',
    desc: 'Tailored gifting solutions that strengthen brands, reward teams, and build lasting relationships.',
  },
];

const AboutUs = () => (
  <div className="about-page">

    {/* ── Hero Banner ───────────────────────────── */}
    <section className="about-hero">
      <div className="about-hero__bg" />
      <div className="about-hero__content container">
        <span className="about-eyebrow">Our Story</span>
        <h1 className="about-hero__title">Where Every Gift<br />Tells a Story</h1>
        <p className="about-hero__sub">
          Crafting personalised and corporate gifting experiences that leave lasting impressions.
        </p>
      </div>
    </section>

    {/* ── Mission ───────────────────────────────── */}
    <section className="about-section">
      <div className="container">
        <div className="about-mission">
          <div className="about-mission__img-wrap">
            <img
              src="https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=700&q=80"
              alt="F Creation Studio"
              className="about-mission__img"
            />
            <div className="about-mission__badge">
              <span className="about-mission__badge-icon">✦</span>
              <div>
                <p className="about-mission__badge-title">F Creation</p>
                <p className="about-mission__badge-sub">Gifting Studio</p>
              </div>
            </div>
          </div>

          <div className="about-mission__text">
            <p className="section-label">Welcome to F Creation Gifting Studio</p>
            <h2 className="section-title" style={{ marginBottom: 24 }}>
              Gifts That Feel<br />Genuinely Personal
            </h2>
            <div className="about-divider" />
            <p className="about-body">
              At the heart of our studio lies a simple belief — <strong>the best gifts are the ones that feel personal.</strong> We specialise in customised and corporate gifting solutions, offering thoughtfully crafted items that leave lasting impressions.
            </p>
            <p className="about-body">
              From name-personalised accessories, customised stationery, mugs, and apparel, to premium corporate hampers — our collection is designed to celebrate every occasion, build brand loyalty, and strengthen relationships.
            </p>
            <p className="about-body">
              Operating from our dedicated studio space, we manage everything in-house — from design to production and packaging — ensuring high quality, consistency, and a personal touch in every order.
            </p>
            <p className="about-body">
              Whether you're looking to surprise a loved one with a one-of-a-kind keepsake, or represent your brand with elegance and originality, we're here to help you make it <strong>unforgettable.</strong>
            </p>
            <div className="about-cta-row">
              <Link to="/products" className="btn btn-primary-warm btn-lg">Explore Our Collection</Link>
              <Link to="/contact" className="btn btn-outline">Get in Touch →</Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Stats ─────────────────────────────────── */}
    <section className="about-stats-section">
      <div className="container">
        <div className="about-stats">
          {stats.map((s) => (
            <div key={s.label} className="about-stat">
              <span className="about-stat__value">{s.value}</span>
              <span className="about-stat__label">{s.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Values ────────────────────────────────── */}
    <section className="about-section about-section--warm">
      <div className="container">
        <div className="section-header" style={{ textAlign: 'center', marginBottom: 48 }}>
          <p className="section-label">What Drives Us</p>
          <h2 className="section-title">Our Core Values</h2>
        </div>
        <div className="about-values">
          {values.map((v) => (
            <div key={v.title} className="value-card">
              <span className="value-card__icon">{v.icon}</span>
              <h3 className="value-card__title">{v.title}</h3>
              <p className="value-card__desc">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Founder ───────────────────────────────── */}
    <section className="about-section">
      <div className="container">
        <div className="section-header" style={{ textAlign: 'center', marginBottom: 48 }}>
          <p className="section-label">Meet Our Founder</p>
          <h2 className="section-title">Passionate Artisans Crafting<br />Memorable Experiences</h2>
        </div>

        <div className="founder-card">
          <div className="founder-card__img-wrap">
            <img
              src="https://res.cloudinary.com/dfl6zlu16/image/upload/v1776785152/1687279357254_txpxvi.jpg"
              alt="Feni Shah — Founder"
              className="founder-card__img"
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80';
              }}
            />
            <div className="founder-card__badge">
              <span>Founder & Creative Director</span>
            </div>
          </div>

          <div className="founder-card__content">
            <div className="founder-quote-mark">"</div>
            <h3 className="founder-card__name">Feni Shah</h3>
            <p className="founder-card__role">Founder, F Creation Gifting Studio</p>
            <div className="about-divider" />
            <p className="about-body">
              She is the creative soul behind our personalised gifting brand. With a deep passion for meaningful design, she curates every piece with love, care, and a personal touch.
            </p>
            <p className="about-body">
              From selecting elegant materials to crafting thoughtful concepts, Feni ensures each gift tells a unique story. Her vision turns everyday items into cherished memories, making every customer feel truly special.
            </p>
            <div className="founder-card__tags">
              {['Personalised Gifting', 'Creative Design', 'Corporate Solutions', 'Quality Craftsmanship'].map((t) => (
                <span key={t} className="founder-tag">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Process ───────────────────────────────── */}
    <section className="about-section about-section--warm">
      <div className="container">
        <div className="section-header" style={{ textAlign: 'center', marginBottom: 48 }}>
          <p className="section-label">How It Works</p>
          <h2 className="section-title">From Idea to Unforgettable Gift</h2>
        </div>
        <div className="process-steps">
          {[
            { step: '01', icon: '💡', title: 'Choose & Customise', desc: 'Browse our collection and personalise with names, messages, or brand artwork.' },
            { step: '02', icon: '🎨', title: 'Design & Craft', desc: 'Our in-house team designs and produces your gift with premium materials.' },
            { step: '03', icon: '📦', title: 'Pack with Love', desc: 'Every order is beautifully packaged with care and ready to impress.' },
            { step: '04', icon: '🚚', title: 'Swift Delivery', desc: 'Delivered across India with speed and care — right to your doorstep.' },
          ].map((s) => (
            <div key={s.step} className="process-step">
              <div className="process-step__num">{s.step}</div>
              <div className="process-step__icon">{s.icon}</div>
              <h4 className="process-step__title">{s.title}</h4>
              <p className="process-step__desc">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── CTA ───────────────────────────────────── */}
    <section className="about-cta-section">
      <div className="container">
        <div className="about-cta-card">
          <span className="about-cta-card__eyebrow">Ready to create something beautiful?</span>
          <h2 className="about-cta-card__title">Start Your Gifting Journey</h2>
          <p className="about-cta-card__sub">Whether it's one gift or a thousand, we bring the same love and care to every single order.</p>
          <div className="about-cta-row">
            <Link to="/products" className="btn btn-primary-warm btn-lg">Shop Now</Link>
            <Link to="/contact" className="btn" style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.4)' }}>Contact Us →</Link>
          </div>
        </div>
      </div>
    </section>

  </div>
);

export default AboutUs;
